/**
 * File: C4ModellingPage.tsx
 * Description: C4 Model architecture visualization and management page
 * Author: System
 * Created: 2025-01-14
 * Last Modified: 2025-01-20
 */

import {
  CubeTransparentIcon,
  SparklesIcon,
  PencilSquareIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { BottomNavigationBar } from '../../components/BottomNavigationBar';
import { ExecutionPlanPanel } from '../../components/ExecutionPlanPanel';
import { ExecutionTracePanel } from '../../components/ExecutionTracePanel';
import { PageHeader } from '../../components/PageHeader';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import { StructurizrEditor } from '../../components/StructurizrEditor';
import {
  STAGE_PAGE_CONTENT_WIDTH_CLASS,
  STAGE_PAGE_WIDTH_CLASS,
} from '../../constants/layout';
import {
  BACK_BUTTON_LABEL,
  CONTINUE_BUTTON_LABEL,
} from '../../constants/navigation';
import { generationService } from '../../services/generationService';
import { projectService } from '../../services/projectService';
import type {
  C4GenerationResponse,
  ExecutionPlanResult,
} from '../../services/projectService';
import { projectStateService } from '../../services/projectStateService';
import { ExecutionStep } from '../../types/execution';
import { ArchitectureSection } from '../../types/generation';
import { ProjectItem, ProjectStatus, ProjectType } from '../../types/project';
import { logger } from '../../utils/logger';
import {
  ExportRow,
  exportRowsAsCsv,
  exportRowsAsJson,
  exportRowsAsXlsx,
  parseFirstMarkdownTable,
} from '../../utils/tableExport';

import { AdrExportView } from './AdrExportView';

type ExecutionPlan = ExecutionStep[] | string[] | null;

// Max iterations configured in C4 agent (from config.yml)
const C4_AGENT_MAX_ITERATIONS = 25;
// Estimated average time per iteration in seconds
const ESTIMATED_SECONDS_PER_ITERATION = 8;
// Maximum progress percentage during generation to prevent reaching 100% before completion
const MAX_PROGRESS_PERCENTAGE = 95;

// MIME type and filename used when downloading the Structurizr DSL file
const DSL_DOWNLOAD_MIME_TYPE = 'text/plain';
const DSL_DOWNLOAD_FILENAME = 'technology-architecture.dsl';

// Base filename for the exported Integration Architecture data-flow table.
const DATA_FLOW_EXPORT_BASENAME = 'data-flow-table';
// Worksheet name used in the data-flow XLSX export.
const DATA_FLOW_SHEET_NAME = 'Data Flows';

/**
 * Identifies which Structurizr diagram sub-category is active on the page.
 */
type DiagramTab = 'c4' | 'technology' | 'interface';

/**
 * Per-tab configuration describing labels, persistence metadata, and the
 * AI service methods used to generate, refine, and trace each diagram type.
 */
interface DiagramTabConfig {
  label: string;
  pageTitle: string;
  pageSubtitle: string;
  diagramTypes: string[];
  saveName: string;
  saveType: string;
  saveDescription: string;
  generateButtonLabel: string;
  generateDescription: string;
  generatingMessage: string;
  refiningMessage: string;
  generationTraceTitle: string;
  refinementTraceTitle: string;
  generationPlanTitle: string;
  refinementPlanTitle: string;
  generate: (projectId: number) => Promise<C4GenerationResponse>;
  refine: (
    projectId: number,
    feedback: string,
    dsl: string,
  ) => Promise<C4GenerationResponse>;
  save: (
    projectId: number,
    diagramData: {
      name: string;
      type: string;
      content: string;
      description?: string;
    },
  ) => Promise<unknown>;
  getGenerationPlan: (projectId: number) => Promise<ExecutionPlanResult>;
  getRefinementPlan?: (projectId: number) => Promise<ExecutionPlanResult>;
  // Whether the tab offers a Structurizr DSL download for BiZZdesign import
  allowDslDownload?: boolean;
}

const DIAGRAM_TAB_CONFIGS: Record<DiagramTab, DiagramTabConfig> = {
  c4: {
    label: 'Conceptual Architecture View & Capability View',
    pageTitle: 'Bizzdesign Integration',
    pageSubtitle:
      'Create and manage C4 model architecture visualizations for your project',
    diagramTypes: ['c4', 'structurizr'],
    saveName: 'C4 Architecture Diagram',
    saveType: 'c4',
    saveDescription: 'Main C4 architecture model for the project',
    generateButtonLabel: 'Generate C4 Model',
    generateDescription:
      'Generate the first C4 draft from project context, ADRs, and brownfield inputs',
    generatingMessage:
      'AI agent is analyzing project context and generating a C4 draft...',
    refiningMessage:
      'AI agent is refining the existing C4 diagram based on your feedback...',
    generationTraceTitle: 'C4 Generation Process',
    refinementTraceTitle: 'C4 Refinement Process',
    generationPlanTitle: 'Execution Plan',
    refinementPlanTitle: 'Refinement Execution Plan',
    generate: (projectId) => projectService.generateC4Diagram(projectId),
    refine: (projectId, feedback, dsl) =>
      projectService.refineC4Diagram(projectId, feedback, dsl),
    save: (projectId, diagramData) =>
      projectService.saveDiagram(projectId, diagramData),
    getGenerationPlan: (projectId) =>
      projectService.getC4GenerationExecutionPlan(projectId),
    getRefinementPlan: (projectId) =>
      projectService.getC4RefinementExecutionPlan(projectId),
    allowDslDownload: true,
  },
  technology: {
    label: 'Implementation & Deployment View',
    pageTitle: 'Technology Architecture (Structurizr)',
    pageSubtitle:
      'Translate the technology architecture diagram into Structurizr DSL',
    diagramTypes: ['technology'],
    saveName: 'Technology Architecture Diagram',
    saveType: 'technology',
    saveDescription:
      'Structurizr DSL translated from the technology architecture diagram',
    generateButtonLabel: 'Generate from Mermaid',
    generateDescription:
      'Translate the technology architecture Mermaid diagram into Structurizr DSL',
    generatingMessage:
      'AI agent is translating the technology architecture diagram into ' +
      'Structurizr DSL...',
    refiningMessage:
      'AI agent is refining the technology architecture DSL based on your ' +
      'feedback...',
    generationTraceTitle: 'Technology Translation Process',
    refinementTraceTitle: 'Technology Refinement Process',
    generationPlanTitle: 'Technology Generation Execution Plan',
    refinementPlanTitle: 'Technology Refinement Execution Plan',
    generate: (projectId) =>
      projectService.generateTechnologyDiagram(projectId),
    refine: (projectId, feedback, dsl) =>
      projectService.refineTechnologyDiagram(projectId, feedback, dsl),
    save: (projectId, diagramData) =>
      projectService.saveTechnologyDiagram(projectId, diagramData),
    getGenerationPlan: (projectId) =>
      projectService.getTechnologyGenerationExecutionPlan(projectId),
    getRefinementPlan: undefined,
    allowDslDownload: true,
  },
  interface: {
    label: 'Application Interface View',
    pageTitle: 'Interface Architecture (Structurizr)',
    pageSubtitle:
      'Turn the C4 System Context into an interface architecture whose ' +
      'connections carry the data-flow IDs',
    diagramTypes: ['interface'],
    saveName: 'Interface Architecture Diagram',
    saveType: 'interface',
    saveDescription:
      'Structurizr DSL interface architecture derived from the C4 System ' +
      'Context and data-flow table',
    generateButtonLabel: 'Generate from System Context',
    generateDescription:
      'Convert the C4 System Context into an interface architecture, ' +
      'labelling each connection with its data-flow Interface_ID',
    generatingMessage:
      'AI agent is converting the C4 System Context into an interface ' +
      'architecture with data-flow IDs...',
    refiningMessage:
      'AI agent is refining the interface architecture DSL based on your ' +
      'feedback...',
    generationTraceTitle: 'Interface Translation Process',
    refinementTraceTitle: 'Interface Refinement Process',
    generationPlanTitle: 'Interface Generation Execution Plan',
    refinementPlanTitle: 'Interface Refinement Execution Plan',
    generate: (projectId) =>
      projectService.generateInterfaceDiagram(projectId),
    refine: (projectId, feedback, dsl) =>
      projectService.refineInterfaceDiagram(projectId, feedback, dsl),
    save: (projectId, diagramData) =>
      projectService.saveInterfaceDiagram(projectId, diagramData),
    getGenerationPlan: (projectId) =>
      projectService.getInterfaceGenerationExecutionPlan(projectId),
    getRefinementPlan: undefined,
    allowDslDownload: true,
  },
};

// Planned views that are not yet available. Rendered greyed out alongside the
// active tabs with a "Soon" badge so users know more views are on the way.
const COMING_SOON_TABS: readonly string[] = [
  'Business Process View',
  'Data in Scope',
  'Data in Glossary',
  'Logical Data View',
];

// Tooltip shown when hovering over a not-yet-available view.
const COMING_SOON_TOOLTIP = 'Coming soon';

// Identifier for the read-only ADR export tab shown alongside the diagram tabs.
const ADR_VIEW_TAB = 'adr' as const;
// Display label and supporting copy for the ADR view tab and page header.
const ADR_VIEW_LABEL = 'ADR View';
const ADR_VIEW_TITLE = 'Bizzdesign Integration';
const ADR_VIEW_SUBTITLE =
  'Review the project ADRs and download them for BiZZdesign import';

// Union of all selectable tabs on the page (diagram tabs plus the ADR view).
type PageTab = DiagramTab | typeof ADR_VIEW_TAB;

// Shared layout classes for the diagram sub-category tab buttons. Keeping these
// in a constant avoids duplicating long class strings and exceeding max-len.
const TAB_BASE_CLASS =
  'flex-1 min-w-[9rem] flex items-center justify-center text-center ' +
  'px-4 py-2.5 rounded-lg text-sm font-medium leading-snug transition-colors';
const TAB_ACTIVE_CLASS = 'bg-[#0891b2] text-white shadow-sm';
const TAB_INACTIVE_CLASS = 'text-gray-600 hover:bg-gray-100';

// Layout classes for the Structurizr DSL download card. The ready/idle variants
// swap the colour scheme to reflect whether a diagram has been generated.
const DSL_CARD_BASE_CLASS =
  'mb-4 flex flex-col gap-3 rounded-xl border p-4 transition-colors ' +
  'sm:flex-row sm:items-center sm:justify-between';
const DSL_CARD_READY_CLASS = 'border-emerald-200 bg-emerald-50/70';
const DSL_CARD_IDLE_CLASS = 'border-gray-200 bg-gray-50';

interface C4ModellingParams extends Record<string, string | undefined> {
  projectId: string;
}

/**
 * C4 Modelling Page component for creating and managing C4 models
 * Provides interface for C4 model architecture visualization
 * @return JSX element for the C4 modelling page
 */
export const C4ModellingPage: React.FC = () => {
  const { projectId } = useParams<C4ModellingParams>();
  const navigate = useNavigate();
  const location = useLocation();
  // Use location.key to force reload when navigating back to the page
  const locationKey = location.key;

  const [project, setProject] = useState<ProjectItem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PageTab>('c4');
  const [structurizrDSL, setStructurizrDSL] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [refinementInput, setRefinementInput] = useState<string>('');
  const [isRefining, setIsRefining] = useState<boolean>(false);
  const [refineError, setRefineError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  // Parsed Integration Architecture data-flow table (headers + rows), used to
  // offer CSV/XLSX/JSON downloads on the Interface Architecture tab.
  const [dataFlowTable, setDataFlowTable] = useState<{
    headers: string[];
    rows: string[][];
  } | null>(null);
  const [c4GenerationExecutionTrace, setC4GenerationExecutionTrace] = useState<ExecutionStep[] | null>(null);
  const [c4RefinementExecutionTrace, setC4RefinementExecutionTrace] = useState<ExecutionStep[] | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  
  // State for detailed C4 execution plans
  const [c4GenerationExecutionPlan, setC4GenerationExecutionPlan] = useState<ExecutionPlan>(null);
  const [c4RefinementExecutionPlan, setC4RefinementExecutionPlan] = useState<ExecutionPlan>(null);
  const [loadingGenerationPlan, setLoadingGenerationPlan] = useState<boolean>(false);
  const [loadingRefinementPlan, setLoadingRefinementPlan] = useState<boolean>(false);
  const [isGenerationPlanExpanded, setIsGenerationPlanExpanded] = useState<boolean>(false);
  const [isRefinementPlanExpanded, setIsRefinementPlanExpanded] = useState<boolean>(false);
  
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Whether the read-only ADR view tab is active (renders its own UI).
  const isAdrView = activeTab === ADR_VIEW_TAB;
  // Active tab configuration drives labels, persistence, and AI service calls.
  // Fall back to the C4 config for the ADR view so diagram-only effects and
  // handlers keep valid references; the ADR view renders separately below.
  const activeTabConfig = isAdrView
    ? DIAGRAM_TAB_CONFIGS.c4
    : DIAGRAM_TAB_CONFIGS[activeTab];

  // Whether a Structurizr DSL diagram has been generated and is displayed.
  // Drives the green "ready" status badge and enables the download action.
  const isDslReady = structurizrDSL.trim().length > 0;

  // Timer effect for tracking elapsed time during generation/refinement
  useEffect(() => {
    if (isGenerating || isRefining) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
      return;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isGenerating, isRefining]);

  // Fetch generation execution plan when expanded
  useEffect(() => {
    const fetchGenerationExecutionPlan = async () => {
      if (!isGenerationPlanExpanded || !projectId) {
        return;
      }

      setLoadingGenerationPlan(true);
      try {
        const plan = await activeTabConfig.getGenerationPlan(
          Number.parseInt(projectId, 10),
        );
        setC4GenerationExecutionPlan(plan as ExecutionPlan);
      } catch (generationError) {
        logger.error('Failed to fetch generation execution plan:', generationError);
        setC4GenerationExecutionPlan(null);
      } finally {
        setLoadingGenerationPlan(false);
      }
    };

    void fetchGenerationExecutionPlan();
  }, [projectId, isGenerationPlanExpanded, activeTabConfig]);

  // Fetch refinement execution plan when expanded
  useEffect(() => {
    const fetchRefinementExecutionPlan = async () => {
      const getRefinementPlan = activeTabConfig.getRefinementPlan;
      if (!isRefinementPlanExpanded || !projectId || !getRefinementPlan) {
        return;
      }

      setLoadingRefinementPlan(true);
      try {
        const plan = await getRefinementPlan(Number.parseInt(projectId, 10));
        setC4RefinementExecutionPlan(plan as ExecutionPlan);
      } catch (refinementError) {
        logger.error('Failed to fetch refinement execution plan:', refinementError);
        setC4RefinementExecutionPlan(null);
      } finally {
        setLoadingRefinementPlan(false);
      }
    };

    void fetchRefinementExecutionPlan();
  }, [projectId, isRefinementPlanExpanded, activeTabConfig]);

  // Helper to format elapsed time
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Helper to estimate progress percentage based on time
  const getEstimatedProgress = (): number => {
    const maxEstimatedSeconds =
      C4_AGENT_MAX_ITERATIONS * ESTIMATED_SECONDS_PER_ITERATION;
    const progress = Math.min(
      (elapsedSeconds / maxEstimatedSeconds) * 100,
      MAX_PROGRESS_PERCENTAGE,
    );
    return Math.round(progress);
  };

  const hasExecutionTraceWithTool = (trace: ExecutionStep[] | null): boolean =>
    Boolean(
      Array.isArray(trace) && trace.some((step) => Boolean(step?.tool)),
    );

  // Load project data and diagrams
  useEffect(() => {
    const loadProjectAndDiagrams = async () => {
      try {
        setIsLoading(true);
        // Reset DSL state when reloading
        setStructurizrDSL('');
        if (!projectId) {
          setError('Invalid project ID');
          return;
        }

        const projectIdNum = Number.parseInt(projectId, 10);
        const loadedProject = await projectService.getProject(projectIdNum);
        setProject(loadedProject);

        // Try to load an existing diagram for the active tab
        try {
          const diagrams = await projectService.getDiagrams(projectIdNum);
          // Find the most recently updated diagram for this tab's types
          const diagram = diagrams
            .filter((d) => activeTabConfig.diagramTypes.includes(d.type))
            .sort((a, b) => {
              const dateA = new Date(a.updated_at || 0).getTime();
              const dateB = new Date(b.updated_at || 0).getTime();
              return dateB - dateA; // Most recent first
            })[0];

          if (diagram?.content) {
            setStructurizrDSL(diagram.content);
          }
        } catch {
          // If no diagrams exist or error loading, continue with template
          // This is expected for new projects
        }

        setError(null);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load project';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjectAndDiagrams();
  }, [projectId, locationKey, activeTabConfig]);

  // Handle stage navigation
  const handleNavigateToStage = (stage: number) => {
    if (!projectId) {
      return;
    }

    const routes = {
      1: `/project/${projectId}/setup`,        // Project Setup
      2: `/project/${projectId}/context`,      // Context Management
      3: `/project/${projectId}/cra`,          // Context Review
      4: `/project/${projectId}/adrs`,         // ADRs
      5: `/project/${projectId}/generation`,   // Architecture Generation
      6: `/project/${projectId}/c4`,            // Bizzdesign Integration / C4 Modeling
      7: `/project/${projectId}/review`,       // Final Review
    };

    const route = routes[stage as keyof typeof routes];
    if (!route) {
      return;
    }
    
    navigate(route);
  };

  // Handle switching between diagram sub-categories (tabs)
  const handleTabChange = (tab: PageTab) => {
    if (tab === activeTab) {
      return;
    }
    setActiveTab(tab);
    setStructurizrDSL('');
    setRefinementInput('');
    setGenerateError(null);
    setRefineError(null);
    setC4GenerationExecutionTrace(null);
    setC4RefinementExecutionTrace(null);
    setC4GenerationExecutionPlan(null);
    setC4RefinementExecutionPlan(null);
  };

  // Handle save diagram
  const handleSaveDiagram = async (content: string) => {
    if (!projectId || !project) {
      return;
    }

    await activeTabConfig.save(Number.parseInt(projectId, 10), {
      name: activeTabConfig.saveName,
      type: activeTabConfig.saveType,
      content,
      description: activeTabConfig.saveDescription,
    });
  };

  // Trigger a browser download of the given text content
  const downloadTextFile = (
    filename: string,
    content: string,
    mimeType: string,
  ) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download the current Structurizr DSL from the editor as a .dsl file
  const handleDownloadDsl = () => {
    if (!structurizrDSL.trim()) {
      return;
    }

    setExportError(null);
    try {
      downloadTextFile(
        DSL_DOWNLOAD_FILENAME,
        structurizrDSL,
        DSL_DOWNLOAD_MIME_TYPE,
      );
    } catch {
      setExportError('Failed to download DSL file');
    }
  };

  // Load and parse the Integration Architecture data-flow table whenever the
  // Interface Architecture tab is active, so it can be downloaded as a table.
  useEffect(() => {
    const loadDataFlowTable = async () => {
      if (activeTab !== 'interface' || !projectId) {
        setDataFlowTable(null);
        return;
      }
      try {
        const response = await generationService.getSection(
          Number.parseInt(projectId, 10),
          ArchitectureSection.INTEGRATION_ARCHITECTURE,
        );
        const content = response.section_content ?? '';
        setDataFlowTable(parseFirstMarkdownTable(content));
      } catch {
        // No integration architecture section yet, or fetch failed.
        setDataFlowTable(null);
      }
    };

    void loadDataFlowTable();
  }, [activeTab, projectId]);

  // Download the parsed data-flow table in the requested format.
  const handleExportDataFlow = (format: 'xlsx' | 'csv' | 'json') => {
    if (!dataFlowTable || dataFlowTable.rows.length === 0) {
      return;
    }

    const { headers, rows } = dataFlowTable;
    const exportRows: ExportRow[] = rows;
    setExportError(null);
    try {
      if (format === 'csv') {
        exportRowsAsCsv(`${DATA_FLOW_EXPORT_BASENAME}.csv`, headers, exportRows);
        return;
      }
      if (format === 'json') {
        const objects = rows.map((row) =>
          Object.fromEntries(headers.map((header, i) => [header, row[i] ?? ''])),
        );
        exportRowsAsJson(`${DATA_FLOW_EXPORT_BASENAME}.json`, objects);
        return;
      }
      exportRowsAsXlsx(
        `${DATA_FLOW_EXPORT_BASENAME}.xlsx`,
        headers,
        exportRows,
        DATA_FLOW_SHEET_NAME,
      );
    } catch {
      setExportError('Failed to download data-flow table');
    }
  };

  // Handle generate diagram with AI
  const handleGenerateC4 = async () => {
    if (!projectId || !project) {
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);

    try {
      const response = await activeTabConfig.generate(
        Number.parseInt(projectId, 10),
      );

      // Capture execution trace from response
      if (response.execution_trace?.steps) {
        const stepsWithNumbers = response.execution_trace.steps.map((step, index) => ({
          step: index + 1,
          ...step,
        }));
        setC4GenerationExecutionTrace(stepsWithNumbers);
      }

      if (response.success && response.content) {
        // Backend already saves the diagram, just update local state
        setStructurizrDSL(response.content);
        await projectStateService.advanceProjectStatus(
          Number.parseInt(projectId, 10),
          ProjectStatus.BIZZDESIGN_INTEGRATION,
        );
      } else {
        setGenerateError(
          response.error_message ??
            response.message ??
            'Failed to generate diagram',
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to generate diagram';
      setGenerateError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle refine diagram with user feedback
  const handleRefineDiagram = async () => {
    if (!projectId || !project || !refinementInput.trim()) {
      return;
    }

    setIsRefining(true);
    setRefineError(null);

    try {
      const response = await activeTabConfig.refine(
        Number.parseInt(projectId, 10),
        refinementInput.trim(),
        structurizrDSL,
      );

      // Capture execution trace from response
      if (response.execution_trace?.steps) {
        const stepsWithNumbers = response.execution_trace.steps.map((step, index) => ({
          step: index + 1,
          ...step,
        }));
        setC4RefinementExecutionTrace(stepsWithNumbers);
      }

      if (response.success && response.content) {
        // Backend already saves the diagram, just update local state
        setStructurizrDSL(response.content);
        await projectStateService.advanceProjectStatus(
          Number.parseInt(projectId, 10),
          ProjectStatus.BIZZDESIGN_INTEGRATION,
        );
        // Clear refinement input after successful refinement
        setRefinementInput('');
      } else {
        setRefineError(
          response.error_message ?? response.message ?? 'Failed to refine diagram',
        );
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to refine diagram';
      setRefineError(errorMessage);
    } finally {
      setIsRefining(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0097a9] mx-auto" />
          <p className="mt-4 text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error ?? 'Project not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-[#0097a9] text-white rounded-lg hover:bg-[#007a8a] transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Stage Navigation Bar */}
      <StageNavigationBar
        currentStage={6}
        projectId={project?.id ?? 0}
        isBrownfield={project?.projectType === ProjectType.BROWN_FIELD}
        projectStatus={project.status as ProjectStatus}
        onStageClick={(_stageId: number) => handleNavigateToStage(_stageId)}
      />

      <div className={`${STAGE_PAGE_WIDTH_CLASS} py-8`}>
        {/* Main Content Area */}
        <div className="space-y-6">
          {/* Page Header */}
          <PageHeader
            title={isAdrView ? ADR_VIEW_TITLE : activeTabConfig.pageTitle}
            subtitle={isAdrView ? ADR_VIEW_SUBTITLE : activeTabConfig.pageSubtitle}
            icon={CubeTransparentIcon}
            iconBgColor="#0891b2"
          />

          {/* Diagram sub-category tabs */}
          <div className="bg-white rounded-xl border border-gray-200 p-1.5 flex flex-wrap items-stretch gap-1.5">
            {(Object.keys(DIAGRAM_TAB_CONFIGS) as DiagramTab[]).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`${TAB_BASE_CLASS} ${
                  activeTab === tab ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS
                }`}
              >
                {DIAGRAM_TAB_CONFIGS[tab].label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => handleTabChange(ADR_VIEW_TAB)}
              className={`${TAB_BASE_CLASS} ${
                isAdrView ? TAB_ACTIVE_CLASS : TAB_INACTIVE_CLASS
              }`}
            >
              {ADR_VIEW_LABEL}
            </button>
            {COMING_SOON_TABS.map((label) => (
              <button
                key={label}
                type="button"
                disabled
                aria-disabled="true"
                title={COMING_SOON_TOOLTIP}
                className={`${TAB_BASE_CLASS} flex-col gap-1 text-gray-400 bg-gray-50 cursor-not-allowed`}
              >
                <span>{label}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                  Soon
                </span>
              </button>
            ))}
          </div>

          {/* Read-only ADR view with download buttons */}
          {isAdrView && <AdrExportView projectId={project.id} />}

          {/* Generate with AI Section */}
          {!isAdrView && (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <SparklesIcon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                    Generate with AI
                      </h3>
                      <p className="text-xs text-gray-500">
                        {activeTabConfig.generateDescription}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleGenerateC4}
                    disabled={isGenerating}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isGenerating
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-t-transparent" />
                    Generating...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4" />
                        {activeTabConfig.generateButtonLabel}
                      </>
                    )}
                  </button>
                </div>

                {/* Progress indicator during generation */}
                {isGenerating && (
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {activeTabConfig.generatingMessage}
                      </span>
                      <span className="text-purple-600 font-medium">
                        {formatElapsedTime(elapsedSeconds)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-1000"
                        style={{ width: `${getEstimatedProgress()}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                  Agent may run up to {C4_AGENT_MAX_ITERATIONS} iterations.
                  Estimated: ~
                      {Math.ceil(
                        (C4_AGENT_MAX_ITERATIONS *
                      ESTIMATED_SECONDS_PER_ITERATION) /
                      60,
                      )}{' '}
                  minutes max
                    </p>
                  </div>
                )}

                {/* Generation Error Message */}
                {generateError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{generateError}</p>
                  </div>
                )}

                {/* Generation Execution Trace */}
                {hasExecutionTraceWithTool(c4GenerationExecutionTrace) && (
                  <div className="mt-4">
                    <ExecutionTracePanel
                      trace={c4GenerationExecutionTrace}
                      title={activeTabConfig.generationTraceTitle}
                    />
                  </div>
                )}
              </div>

              {/* Refine Diagram Section */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <PencilSquareIcon className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                  Refine Diagram
                    </h3>
                    <p className="text-xs text-gray-500">
                  Provide feedback to update an existing diagram instead of
                  starting from scratch.
                    </p>
                  </div>
                </div>

                {/* Refinement Input */}
                <div className="space-y-3">
                  <textarea
                    value={refinementInput}
                    onChange={(e) => setRefinementInput(e.target.value)}
                    placeholder="Describe what changes you'd like to make to the diagram (e.g., 'Add more detail to the authentication flow', 'Include external API integrations', 'Simplify the container view')"
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    rows={3}
                    disabled={isRefining}
                  />

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {activeTabConfig.getRefinementPlan
                        ? 'Use feedback to update the current diagram without rebuilding the first draft.'
                        : 'Generate a diagram first to enable refinement'}
                    </div>
                    <button
                      onClick={handleRefineDiagram}
                      disabled={
                        isRefining || !refinementInput.trim() || !structurizrDSL
                      }
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isRefining || !refinementInput.trim() || !structurizrDSL
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-orange-600 text-white hover:bg-orange-700'
                      }`}
                    >
                      {isRefining ? (
                        <>
                          <div
                            className="animate-spin rounded-full h-4 w-4 border-2 
                        border-orange-600 border-t-transparent"
                          />
                      Refining...
                        </>
                      ) : (
                        <>
                          <PencilSquareIcon className="w-4 h-4" />
                      Refine Diagram
                        </>
                      )}
                    </button>
                  </div>

                  {/* Progress indicator during refinement */}
                  {isRefining && (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                          {activeTabConfig.refiningMessage}
                        </span>
                        <span className="text-orange-600 font-medium">
                          {formatElapsedTime(elapsedSeconds)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-orange-600 h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${getEstimatedProgress()}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                    Agent may run up to {C4_AGENT_MAX_ITERATIONS} iterations.
                    Estimated: ~
                        {Math.ceil(
                          (C4_AGENT_MAX_ITERATIONS *
                        ESTIMATED_SECONDS_PER_ITERATION) /
                        60,
                        )}{' '}
                    minutes max
                      </p>
                    </div>
                  )}
                </div>

                {/* Refinement Error Message */}
                {refineError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{refineError}</p>
                  </div>
                )}

                {/* Refinement Execution Trace */}
                {hasExecutionTraceWithTool(c4RefinementExecutionTrace) && (
                  <div className="mt-4">
                    <ExecutionTracePanel
                      trace={c4RefinementExecutionTrace}
                      title={activeTabConfig.refinementTraceTitle}
                    />
                  </div>
                )}
              </div>

              {/* Structurizr DSL download (technology architecture only) */}
              {activeTabConfig.allowDslDownload && (
                <div
                  className={`${DSL_CARD_BASE_CLASS} ${
                    isDslReady ? DSL_CARD_READY_CLASS : DSL_CARD_IDLE_CLASS
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {isDslReady ? (
                      <CheckCircleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-emerald-500" />
                    ) : (
                      <ArrowDownTrayIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-gray-400" />
                    )}
                    <div>
                      <p
                        className={`text-sm font-semibold tracking-tight ${
                          isDslReady ? 'text-emerald-700' : 'text-gray-700'
                        }`}
                      >
                        {isDslReady
                          ? 'Structurizr DSL ready'
                          : 'Structurizr DSL not generated yet'}
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
                        Download the Structurizr DSL (.dsl) to import this diagram
                        into BiZZdesign Enterprise Studio.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadDsl}
                    disabled={!isDslReady}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#0891b2] px-4 py-2 text-sm font-semibold tracking-tight text-white shadow-sm transition-colors hover:bg-[#0e7490] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Download DSL (.dsl)
                  </button>
                </div>
              )}

              {/* Integration Architecture data-flow table download (interface tab) */}
              {activeTab === 'interface' && (
                <div className="mb-4 flex flex-col gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-600">
                    {dataFlowTable && dataFlowTable.rows.length > 0
                      ? 'Download the Integration Architecture data-flow table ' +
                    `(${dataFlowTable.rows.length} interface${
                      dataFlowTable.rows.length === 1 ? '' : 's'
                    }).`
                      : 'No data-flow table found yet. Generate the Integration ' +
                    'Architecture section first.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleExportDataFlow('xlsx')}
                      disabled={!dataFlowTable || dataFlowTable.rows.length === 0}
                      title="Download data-flow table as Excel (.xlsx)"
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                  XLSX
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExportDataFlow('csv')}
                      disabled={!dataFlowTable || dataFlowTable.rows.length === 0}
                      title="Download data-flow table as CSV"
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                  CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExportDataFlow('json')}
                      disabled={!dataFlowTable || dataFlowTable.rows.length === 0}
                      title="Download data-flow table as JSON"
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                  JSON
                    </button>
                  </div>
                </div>
              )}

              {exportError && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  {exportError}
                </div>
              )}

              {/* Structurizr Editor */}
              <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-base font-semibold tracking-tight text-gray-900">
                    Structurizr Diagram
                  </h3>
                  {isDslReady && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold tracking-tight text-emerald-700">
                      <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                      Diagram enabled
                    </span>
                  )}
                </div>
                <StructurizrEditor
                  initialContent={structurizrDSL}
                  onChange={setStructurizrDSL}
                  onSave={handleSaveDiagram}
                  height="800px"
                />
              </div>
            </>
          )}
        </div>

        {/* Execution Plans Footer */}
        {!isAdrView && (
          <div className={`${STAGE_PAGE_CONTENT_WIDTH_CLASS} mt-8 space-y-6`}>
            {/* Generation Execution Plan */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <ExecutionPlanPanel
                title={activeTabConfig.generationPlanTitle}
                executionPlan={c4GenerationExecutionPlan}
                isLoading={loadingGenerationPlan}
                isExpanded={isGenerationPlanExpanded}
                onToggleExpanded={() => setIsGenerationPlanExpanded(!isGenerationPlanExpanded)}
              />
            </div>

            {/* Refinement Execution Plan (only for tabs that support it) */}
            {activeTabConfig.getRefinementPlan && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <ExecutionPlanPanel
                  title={activeTabConfig.refinementPlanTitle}
                  executionPlan={c4RefinementExecutionPlan}
                  isLoading={loadingRefinementPlan}
                  isExpanded={isRefinementPlanExpanded}
                  onToggleExpanded={() => setIsRefinementPlanExpanded(!isRefinementPlanExpanded)}
                />
              </div>
            )}
          </div>
        )}

        <BottomNavigationBar
          backAction={{
            label: BACK_BUTTON_LABEL,
            onClick: () => handleNavigateToStage(5),
          }}
          primaryActions={[
            {
              label: CONTINUE_BUTTON_LABEL,
              onClick: async () => {
                if (projectId && structurizrDSL.trim().length > 0) {
                  await projectStateService.advanceProjectStatus(
                    Number(projectId),
                    ProjectStatus.COMPLETED,
                  );
                }
                handleNavigateToStage(7);
              },
              variant: 'primary',
            },
          ]}
        />
      </div>
    </div>
  );
};
